import React, { useCallback, useMemo } from "react";
import useDeepMemo from "domains/commons/hooks/useDeepMemo";
import { PersistedStateKey } from "domains/commons/hooks/usePersistedState";
import { FmFileModel } from "domains/file-manager/interfacesV2";
import useRouter from "domains/navigation/hooks/useRouter";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import { useDeleteModelsByModelIdMutation } from "infra/api/generated/api";

import FileManager, { FileManagerProps } from "../FileManagerV2";
import HeaderFilterAuthor, {
  FmHeaderFilterAuthorProps,
} from "../FileManagerV2/FmHeaderFilterAuthor";
import HeaderFilterCollection, {
  FmHeaderFilterCollectionProps,
} from "../FileManagerV2/FmHeaderFilterCollection";
import HeaderFilterDate, {
  FmHeaderFilterDateProps,
} from "../FileManagerV2/FmHeaderFilterDate";
import HeaderSort, { FmHeaderSortProps } from "../FileManagerV2/FmHeaderSort";
import { FmSelectionActionsProps } from "../FileManagerV2/FmSelectionActions";

import Grid from "./Grid";
import SelectionActions, {
  SelectionActionsCustomProps,
  SelectionActionsProps,
} from "./SelectionActions";

export type FileManagerModelProps<ES extends object = object> = Omit<
  FileManagerProps<"model", object, object, object, object, ES>,
  | "jobs"
  | "type"
  | "settingsCacheKey"
  | "gridProps"
  | "GridComponent"
  | "listProps"
  | "ListComponent"
  | "virtualFilesProps"
  | "VirtualFilesComponent"
  | "selectionActionsProps"
  | "SelectionActionsComponent"
  | "onCardClick"
  | "withLink"
  | "assetZoomPriorityLevel"
> &
  Partial<
    Pick<
      FileManagerProps<"model">,
      "settingsCacheKey" | "onCardClick" | "withLink"
    >
  > & {
    ignoreActions?: SelectionActionsProps["ignoreActions"];

    headerFilterAuthorProps?: FmHeaderFilterAuthorProps;
    headerFilterDateProps?: FmHeaderFilterDateProps;
    headerFilterCollectionProps?: FmHeaderFilterCollectionProps;
    headerSortProps?: FmHeaderSortProps;

    onDelete?: (files: FmFileModel[]) => void;
    onUndoDelete?: (files: FmFileModel[]) => void;

    SelectionActionsComponent?: React.FunctionComponent<
      FmSelectionActionsProps<"model", SelectionActionsCustomProps>
    >;

    filterCount?: number;
  };

function FileManagerModelWithoutMemo<ES extends object = object>({
  settingsCacheKey,
  onCardClick,
  withLink,

  ignoreActions: unmemoizedIgnoreActions,

  headerFilterAuthorProps,
  headerFilterDateProps,
  headerFilterCollectionProps,
  headerSortProps,
  headerFilterContent,
  headerLeftContent,
  headerRightContent,

  onDelete,
  onUndoDelete,

  SelectionActionsComponent,

  filterCount,

  ...props
}: FileManagerModelProps<ES>) {
  const router = useRouter();
  const { successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { selectedProject } = useTeamContext();
  const [deleteModelTrigger, { isLoading: isDeleting }] =
    useDeleteModelsByModelIdMutation();

  const ignoreActions = useDeepMemo(unmemoizedIgnoreActions);

  const cacheKey = settingsCacheKey ?? PersistedStateKey.FILE_MANAGER_MODEL_V2;

  // ----------------------------------

  const handleCardClick = useCallback(
    (file: FmFileModel) => {
      void router.push(
        {
          pathname: "/models/[id]",
          query: { id: file.id },
        },
        undefined,
        { scroll: false }
      );
    },
    [router]
  );

  const handleDelete = useCallback(
    async (files: FmFileModel[]) => {
      try {
        onDelete?.(files);

        await Promise.all(
          files.map((file) => {
            void deleteModelTrigger({
              projectId: selectedProject.id,
              modelId: file.id,
            });
            Track(AnalyticsEvents.Model.Deleted, {
              modelId: file.id,
            });
          })
        );

        successToast({
          title: "Model(s) deleted",
        });
      } catch (error) {
        onUndoDelete?.(files);
        handleApiError(error, "There was an error deleting the model(s)");
      }
    },
    [
      handleApiError,
      deleteModelTrigger,
      successToast,
      selectedProject.id,
      onDelete,
      onUndoDelete,
    ]
  );

  // ----------------------------------

  const selectionActionsProps = useMemo<
    Required<
      Pick<
        FileManagerProps<"model", object, SelectionActionsCustomProps>,
        "SelectionActionsComponent" | "selectionActionsProps"
      >
    >
  >(
    () => ({
      SelectionActionsComponent: SelectionActionsComponent ?? SelectionActions,
      selectionActionsProps: {
        isDeleting,
        onDeleteClick: handleDelete,
        ignoreActions,
      },
    }),
    [SelectionActionsComponent, isDeleting, handleDelete, ignoreActions]
  );

  return (
    <FileManager
      type="model"
      {...selectionActionsProps}
      GridComponent={Grid}
      settingsCacheKey={cacheKey}
      initialColumnsCount={4}
      onCardClick={onCardClick ?? handleCardClick}
      withLink={withLink}
      filterCount={filterCount}
      headerFilterContent={
        <>
          {!!headerFilterAuthorProps && (
            <HeaderFilterAuthor {...headerFilterAuthorProps} />
          )}

          {!!headerFilterDateProps && (
            <HeaderFilterDate {...headerFilterDateProps} />
          )}

          {!!headerFilterCollectionProps && (
            <HeaderFilterCollection {...headerFilterCollectionProps} />
          )}

          {headerFilterContent}
        </>
      }
      headerLeftContent={headerLeftContent}
      headerRightContent={
        <>
          {!!headerSortProps && <HeaderSort {...headerSortProps} />}
          {headerRightContent}
        </>
      }
      {...props}
    />
  );
}

const FileManagerModel = React.memo(
  FileManagerModelWithoutMemo
) as typeof FileManagerModelWithoutMemo;

export default FileManagerModel;
